import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscriptionAPI } from '../services/api';
import { Check, Sparkles, ArrowLeft, Loader2 } from 'lucide-react';
import Logo from '../components/Logo';
import Footer from '../components/Footer';

export default function Plans() {
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [plansResponse, subResponse] = await Promise.all([
        subscriptionAPI.getPlans(),
        subscriptionAPI.getCurrentSubscription()
      ]);
      setPlans(plansResponse.data.plans);
      setCurrentPlan(subResponse.data.subscription.plan_type);
    } catch (error) {
      console.error('Error loading plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planType) => {
    if (planType === 'free') return;
    
    setUpgrading(planType);
    try {
      const response = await subscriptionAPI.createCheckout(planType);
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      alert('Failed to start checkout. Please try again.');
      setUpgrading(null);
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #e8f5e9 0%, #e3f2fd 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ fontSize: '18px', color: '#2e7d32' }}>Loading plans...</div>
      </div>
    );
  }

  const planColors = {
    free: { main: '#9e9e9e', light: '#f5f5f5', gradient: 'linear-gradient(135deg, #bdbdbd 0%, #9e9e9e 100%)' },
    teacher: { main: '#66bb6a', light: '#e8f5e9', gradient: 'linear-gradient(135deg, #81c784 0%, #66bb6a 100%)' },
    school: { main: '#42a5f5', light: '#e3f2fd', gradient: 'linear-gradient(135deg, #64b5f6 0%, #42a5f5 100%)' }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #e8f5e9 0%, #e3f2fd 100%)'
    }}>
      {/* Header */}
      <div style={{
        background: 'white',
        borderBottom: '1px solid #e0e0e0',
        padding: '20px 32px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
      }}>
        <button
          onClick={() => navigate('/dashboard')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            background: 'white',
            border: '2px solid #e0e0e0',
            borderRadius: '8px',
            color: '#424242',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => e.target.style.borderColor = '#81c784'}
          onMouseOut={(e) => e.target.style.borderColor = '#e0e0e0'}
        >
          <ArrowLeft size={18} />
          Back
        </button>
        <Logo size="medium" showTagline={false} />
      </div>

      {/* Plans Grid */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '48px 32px' }}>
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <h2 style={{ fontSize: '32px', fontWeight: '700', color: '#2e7d32', marginBottom: '16px' }}>
            Simple, Transparent Pricing
          </h2>
          <p style={{ fontSize: '18px', color: '#666' }}>
            Choose the plan that's right for you. Upgrade or downgrade anytime.
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '32px',
          maxWidth: '1100px',
          margin: '0 auto'
        }}>
          {plans.map((plan) => {
            const isCurrentPlan = plan.id === currentPlan;
            const colors = planColors[plan.id];
            const features = typeof plan.features === 'string' ? JSON.parse(plan.features) : plan.features;

            return (
              <div
                key={plan.id}
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  padding: '32px',
                  boxShadow: isCurrentPlan ? '0 8px 24px rgba(102, 187, 106, 0.3)' : '0 4px 12px rgba(0,0,0,0.08)',
                  border: isCurrentPlan ? `3px solid ${colors.main}` : '3px solid transparent',
                  position: 'relative',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  transform: isCurrentPlan ? 'scale(1.05)' : 'scale(1)'
                }}
                onMouseOver={(e) => {
                  if (!isCurrentPlan) {
                    e.currentTarget.style.transform = 'translateY(-8px)';
                    e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.15)';
                  }
                }}
                onMouseOut={(e) => {
                  if (!isCurrentPlan) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                  }
                }}
              >
                {isCurrentPlan && (
                  <div style={{
                    position: 'absolute',
                    top: '-12px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    padding: '6px 20px',
                    background: colors.gradient,
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: '700',
                    borderRadius: '20px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Current Plan
                  </div>
                )}

                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    background: colors.gradient,
                    borderRadius: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px'
                  }}>
                    <Sparkles size={32} color="white" />
                  </div>
                  <h3 style={{ fontSize: '24px', fontWeight: '700', color: colors.main, marginBottom: '8px' }}>
                    {plan.name}
                  </h3>
                  <div style={{ fontSize: '48px', fontWeight: '700', color: '#424242' }}>
                    ${parseFloat(plan.price_monthly_usd).toFixed(0)}
                    <span style={{ fontSize: '18px', fontWeight: '400', color: '#999' }}>/month</span>
                  </div>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <div style={{
                    padding: '12px',
                    background: colors.light,
                    borderRadius: '8px',
                    textAlign: 'center',
                    marginBottom: '16px'
                  }}>
                    <span style={{ fontSize: '16px', fontWeight: '700', color: colors.main }}>
                      {plan.generations_per_month === null ? '∞ Unlimited' : plan.generations_per_month} generations/month
                    </span>
                  </div>

                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    <li style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                      <Check size={20} color={colors.main} />
                      <span style={{ color: '#424242' }}>
                        {features.file_uploads ? 'File uploads' : 'Text input only'}
                      </span>
                    </li>
                    <li style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                      <Check size={20} color={colors.main} />
                      <span style={{ color: '#424242' }}>
                        {features.material_history ? 'Material history' : 'No history'}
                      </span>
                    </li>
                    <li style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                      <Check size={20} color={colors.main} />
                      <span style={{ color: '#424242' }}>
                        {features.download_formats?.join(', ').toUpperCase() || 'TXT'} downloads
                      </span>
                    </li>
                    {features.priority_support && (
                      <li style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                        <Check size={20} color={colors.main} />
                        <span style={{ color: '#424242' }}>Priority support</span>
                      </li>
                    )}
                    {features.analytics && (
                      <li style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                        <Check size={20} color={colors.main} />
                        <span style={{ color: '#424242' }}>Usage analytics</span>
                      </li>
                    )}
                  </ul>
                </div>

                <button
                  onClick={() => handleUpgrade(plan.id)}
                  disabled={isCurrentPlan || upgrading === plan.id || plan.id === 'free'}
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: isCurrentPlan ? '#e0e0e0' : (plan.id === 'free' ? '#f5f5f5' : colors.gradient),
                    color: isCurrentPlan ? '#999' : (plan.id === 'free' ? '#999' : 'white'),
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '700',
                    cursor: (isCurrentPlan || plan.id === 'free') ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'transform 0.2s'
                  }}
                  onMouseOver={(e) => {
                    if (!isCurrentPlan && plan.id !== 'free') {
                      e.target.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                >
                  {upgrading === plan.id ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      Processing...
                    </>
                  ) : isCurrentPlan ? (
                    'Current Plan'
                  ) : plan.id === 'free' ? (
                    'Free Plan'
                  ) : (
                    `Upgrade to ${plan.name}`
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>
<Footer />
    </div>
  );
}